import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Sparkles,
  Calendar,
  Clock,
  MapPin,
  Compass,
  Users,
  ShieldCheck,
  Minus,
  Plus,
  Zap,
} from 'lucide-react';
import { useLocation } from '@context/LocationContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { useAuth } from '@context/AuthContext';
import Header from '@components/layout/Header';
import MapboxInlinePicker from '@components/map/MapboxInlinePicker';
import GooglePlacesSearch from '@components/map/GooglePlacesSearch';
import api from '@utils/api';
import { trackCreateActivity } from '@utils/analytics';
import toast from 'react-hot-toast';
import { openGoogleMapsDirectionsToDestination, formatActivityDate, getCategoryInfo, formatCountNoun } from '@utils/helpers';
import { reverseGeocodeLatLng } from '@utils/googleGeocode';
import { UNVERIFIED_HOST_ACTIVITY_LIMIT } from '@/constants/activityHostLimits';

const CATEGORIES = [
  { id: 'sports', label: 'Sports', emoji: '🏸' },
  { id: 'fitness', label: 'Fitness', emoji: '💪' },
  { id: 'music', label: 'Music', emoji: '🎵' },
  { id: 'study', label: 'Study', emoji: '📚' },
  { id: 'travel', label: 'Travel', emoji: '✈️' },
  { id: 'learning', label: 'Learning', emoji: '🧠' },
  { id: 'social', label: 'Social', emoji: '🎉' },
  { id: 'outdoors', label: 'Outdoors', emoji: '🌿' },
  { id: 'creative', label: 'Creative', emoji: '🎨' },
  { id: 'gaming', label: 'Gaming', emoji: '🎮' },
  { id: 'food', label: 'Food & Cooking', emoji: '🍳' },
];

const WIZARD_STEPS = [
  { label: 'The Plan', headline: "What's the plan?", emoji: '🎯', sub: "Pick a vibe — we'll handle the rest." },
  { label: 'Location', headline: 'Pin the spot', emoji: '📍', sub: 'Search or drag the map so others know where to meet.' },
  { label: 'When', headline: "When's it happening?", emoji: '📅', sub: 'Choose a date and time that works for you.' },
  { label: 'The Crew', headline: "Who's invited?", emoji: '👋', sub: 'Set who can join and how many spots to open.' },
  { label: 'Launch', headline: 'Ready to go?', emoji: '✨', sub: 'Give it a quick look, then publish.' },
];

const PARTICIPANT_PRESETS = [2, 3, 4, 5, 6, 8, 10, 15, 20];
const PARTICIPANTS_MIN = 2;
const PARTICIPANTS_MAX = 20;

const clampParticipants = (n) => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return PARTICIPANTS_MIN;
  return Math.min(PARTICIPANTS_MAX, Math.max(PARTICIPANTS_MIN, v));
};

const isPresetParticipantCount = (n) => PARTICIPANT_PRESETS.includes(clampParticipants(n));

/** Match `ActivityCard` hero gradients so the preview feels like Explore. */
const CATEGORY_GRADIENTS = {
  sports: 'from-amber-400 via-orange-400 to-orange-500',
  fitness: 'from-rose-400 via-red-400 to-red-500',
  learning: 'from-emerald-400 via-teal-400 to-teal-500',
  social: 'from-violet-400 via-purple-400 to-purple-500',
  outdoors: 'from-lime-400 via-green-400 to-emerald-500',
  creative: 'from-pink-400 via-fuchsia-400 to-fuchsia-500',
  gaming: 'from-indigo-400 via-blue-500 to-blue-600',
  food: 'from-orange-400 via-amber-400 to-amber-500',
  music: 'from-rose-400 via-pink-400 to-pink-500',
  study: 'from-cyan-400 via-sky-400 to-blue-500',
  travel: 'from-sky-400 via-blue-400 to-indigo-500',
};

const toLocalDateValue = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toLocalTimeValue = (dateObj) => {
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const parseLocalDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** If local date+time is not in the future, bump to at least now + 1 minute (edit / stale UI). */
const ensureFutureLocalSlot = (dateStr, timeStr) => {
  const d = parseLocalDateTime(dateStr, timeStr);
  const now = Date.now();
  if (d && d.getTime() > now) return { date: dateStr, time: timeStr };
  const bump = new Date(now + 60_000);
  return { date: toLocalDateValue(bump), time: toLocalTimeValue(bump) };
};

const addDaysLocal = (fromDateObj, days) => {
  const d = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

const CreateActivityPage = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { user, updateUser } = useAuth();
  const { googleMapsApiKey } = usePublicConfig();
  const { location: userLoc, locationName, deviceLocationName } = useLocation();
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  /** After the user drags/taps the map pin, do not overwrite coords when device location resolves or updates. */
  const mapPinUserAdjustedRef = useRef(false);
  /** Ignore stale reverse-geocode responses when the pin moves again before the request finishes. */
  const mapGeocodeSeqRef = useRef(0);
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(isEditMode);
  const [locationLockedForEdit, setLocationLockedForEdit] = useState(false);
  const [step, setStep] = useState(1);
  /** Step 3: show inline date field (Lovable) instead of only opening native calendar. */
  const [customDateExpanded, setCustomDateExpanded] = useState(false);
  /** Step 5: avoid instant publish from the same gesture that tapped Continue (double-tap / trailing touch). */
  const [reviewPublishEnabled, setReviewPublishEnabled] = useState(false);
  /** Step 4: custom participant count stepper open (or forced when value is not a preset). */
  const [participantsCustomOpen, setParticipantsCustomOpen] = useState(false);
  const [coords, setCoords] = useState([userLoc.lng, userLoc.lat]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    locationAddress: '',
    date: '',
    time: '',
    maxParticipants: 4,
    emoji: '',
    allowedGender: 'everyone',
    verifiedOnly: false,
  });

  // Keep coords in sync with context if user hasn't picked a custom location or moved the map pin
  useEffect(() => {
    if (isEditMode) return;
    if (!form.locationAddress && !mapPinUserAdjustedRef.current) {
      setCoords([userLoc.lng, userLoc.lat]);
    }
  }, [isEditMode, userLoc.lng, userLoc.lat, form.locationAddress]);

  const handleMapCoordinatesChange = useCallback(
    async ([lng, lat]) => {
      if (locationLockedForEdit) return;
      mapPinUserAdjustedRef.current = true;
      setCoords([lng, lat]);
      const seq = ++mapGeocodeSeqRef.current;
      const formatted = await reverseGeocodeLatLng(lat, lng, googleMapsApiKey);
      if (seq !== mapGeocodeSeqRef.current) return;
      const short = formatted
        ? formatted.split(',').slice(0, 3).join(',').trim()
        : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setForm((prev) => ({ ...prev, locationAddress: short }));
    },
    [googleMapsApiKey, locationLockedForEdit],
  );

  useEffect(() => {
    if (!isEditMode) return;

    let mounted = true;
    const prefill = async () => {
      setPrefillLoading(true);
      try {
        const { data } = await api.get(`/activities/${editId}`);
        if (!mounted) return;
        const activity = data.activity;
        const creatorId = activity?.creator?._id || activity?.creator;
        const isOwner = creatorId && String(creatorId) === String(user?._id);
        if (!isOwner) {
          toast.error('Only the creator can edit this vibe');
          navigate(`/activity/${editId}`, { replace: true });
          return;
        }
        const hasOtherParticipants = (activity?.participants || []).some(
          (participant) => String(participant?._id || participant) !== String(creatorId),
        );
        setLocationLockedForEdit(hasOtherParticipants);

        const dt = activity?.dateTime ? new Date(activity.dateTime) : null;
        let date = dt ? toLocalDateValue(dt) : '';
        let time = dt ? toLocalTimeValue(dt) : '';
        if (date && time) {
          const slot = ensureFutureLocalSlot(date, time);
          date = slot.date;
          time = slot.time;
        }

        setForm({
          title: activity?.title || '',
          description: activity?.description || '',
          category: activity?.category || '',
          locationAddress: activity?.location?.address || '',
          date,
          time,
          maxParticipants: clampParticipants(activity?.maxParticipants ?? 4),
          emoji: activity?.emoji || '',
          allowedGender: activity?.allowedGender || 'everyone',
          verifiedOnly:
            user?.faceVerified === true ? activity?.verifiedOnly !== false : false,
        });
        if (Array.isArray(activity?.location?.coordinates) && activity.location.coordinates.length === 2) {
          setCoords(activity.location.coordinates);
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not load vibe for editing');
        navigate('/feed', { replace: true });
      } finally {
        if (mounted) setPrefillLoading(false);
      }
    };

    prefill();
    return () => {
      mounted = false;
    };
  }, [editId, isEditMode, navigate, user?._id, user?.faceVerified]);

  /** Unverified hosts cannot offer verified-only join; keep toggle off and payload safe. */
  useEffect(() => {
    if (user?.faceVerified === true) return;
    setForm((prev) => (prev.verifiedOnly ? { ...prev, verifiedOnly: false } : prev));
  }, [user?.faceVerified]);

  useEffect(() => {
    if (!isEditMode) {
      setStep(1);
      setLocationLockedForEdit(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (step !== 3) {
      setCustomDateExpanded(false);
      return;
    }
    const today = toLocalDateValue(new Date());
    const tomorrow = toLocalDateValue(addDaysLocal(new Date(), 1));
    if (form.date && form.date !== today && form.date !== tomorrow) {
      setCustomDateExpanded(true);
    }
  }, [step, form.date]);

  useEffect(() => {
    if (step !== 5) {
      setReviewPublishEnabled(false);
      return;
    }
    setReviewPublishEnabled(false);
    const enableId = window.setTimeout(() => setReviewPublishEnabled(true), 450);
    return () => {
      window.clearTimeout(enableId);
    };
  }, [step]);

  /** Keep wizard content aligned to the top when changing steps (window scroll). */
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [step]);

  useEffect(() => {
    if (step !== 4) {
      setParticipantsCustomOpen(false);
      return;
    }
    if (!isPresetParticipantCount(form.maxParticipants)) {
      setParticipantsCustomOpen(true);
    }
  }, [step, form.maxParticipants]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const bumpMaxParticipants = (delta) => {
    setForm((prev) => ({
      ...prev,
      maxParticipants: clampParticipants((Number(prev.maxParticipants) || PARTICIPANTS_MIN) + delta),
    }));
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const goNext = () => {
    if (step === 1) {
      if (!form.category?.trim()) {
        toast.error('Please select a category.');
        return;
      }
      if (!form.title?.trim()) {
        toast.error('Give your vibe a title.');
        return;
      }
      if (!form.description?.trim()) {
        toast.error('Tell people what to expect.');
        return;
      }
    }
    if (step === 3) {
      const localDt = parseLocalDateTime(form.date, form.time);
      if (!localDt || localDt.getTime() <= Date.now()) {
        toast.error('Pick a future date and time.');
        return;
      }
    }
    setStep((s) => Math.min(5, s + 1));
  };

  const handlePlaceSelect = ({ lat, lng, name }) => {
    if (locationLockedForEdit) return;
    mapPinUserAdjustedRef.current = true;
    const short = name.split(',').slice(0, 3).join(',').trim();
    update('locationAddress', short);
    setCoords([lng, lat]);
  };

  const applyCurrentLocation = useCallback(async () => {
    if (locationLockedForEdit) return;
    mapPinUserAdjustedRef.current = false;
    const lng = userLoc.lng;
    const lat = userLoc.lat;
    setCoords([lng, lat]);
    const seq = ++mapGeocodeSeqRef.current;
    const formatted = await reverseGeocodeLatLng(lat, lng, googleMapsApiKey);
    if (seq !== mapGeocodeSeqRef.current) return;
    const unusable = new Set(['', 'Detecting...', 'Location unavailable']);
    const fromContext = [locationName, deviceLocationName].find((n) => n && !unusable.has(n));
    const short = formatted
      ? formatted.split(',').slice(0, 3).join(',').trim()
      : fromContext || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    setForm((prev) => ({ ...prev, locationAddress: short }));
  }, [userLoc.lng, userLoc.lat, googleMapsApiKey, locationName, deviceLocationName, locationLockedForEdit]);

  /** When the user did not pick a place from search, still persist a human-readable area (device / map context). */
  const resolveLocationAddressForSubmit = () => {
    const trimmed = (form.locationAddress || '').trim();
    if (trimmed) return trimmed;

    const threshold = 0.00055;
    const coordsNearUser =
      Math.abs(coords[0] - userLoc.lng) < threshold && Math.abs(coords[1] - userLoc.lat) < threshold;

    const unusable = new Set(['', 'Detecting...', 'Location unavailable']);
    const fromContext = [locationName, deviceLocationName].find((n) => n && !unusable.has(n));

    if (coordsNearUser) return fromContext || 'Current location';
    return fromContext || 'Selected map location';
  };

  const saveActivity = async () => {
    const localDt = parseLocalDateTime(form.date, form.time);
    if (!localDt || localDt.getTime() <= Date.now()) {
      toast.error('Cannot create a vibe in the past.');
      return;
    }
    if (!form.category?.trim()) {
      toast.error('Please select a category.');
      return;
    }
    if (!form.title?.trim()) {
      toast.error('Give your vibe a title.');
      return;
    }
    if (!form.description?.trim()) {
      toast.error('Tell people what to expect.');
      return;
    }
    setLoading(true);
    try {
      const dateTime = localDt.toISOString();
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        coordinates: coords,
        dateTime,
        maxParticipants: clampParticipants(form.maxParticipants),
        locationAddress: resolveLocationAddressForSubmit(),
        emoji: form.emoji,
        allowedGender: form.allowedGender,
        verifiedOnly: user?.faceVerified === true && form.verifiedOnly,
      };
      const { data } = isEditMode
        ? await api.put(`/activities/${editId}`, payload)
        : await api.post('/activities', payload);

      if (!isEditMode) {
        trackCreateActivity(form.category);
      }

      if (!isEditMode && data.hostedActivitiesCreatedCount != null && user) {
        updateUser({ ...user, hostedActivitiesCreatedCount: data.hostedActivitiesCreatedCount });
      }

      toast.success(isEditMode ? 'Vibe updated!' : 'Vibe created!');
      navigate(`/activity/${data.activity._id}`);
    } catch (err) {
      const d = err.response?.data;
      const firstField = Array.isArray(d?.errors) && d.errors[0]?.message;
      toast.error(firstField || d?.message || `Failed to ${isEditMode ? 'update' : 'create'} vibe`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveActivity();
  };

  if (prefillLoading) {
    return (
      <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
        <Header />
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 lg:pb-10 pt-6">
          <p className="text-sm text-surface-500 dark:text-surface-400">Loading vibe...</p>
        </div>
      </div>
    );
  }

  const stepMeta = WIZARD_STEPS[step - 1];
  const previewCategory = getCategoryInfo(form.category);
  const previewGradient = CATEGORY_GRADIENTS[form.category] || CATEGORY_GRADIENTS.sports;
  const previewHeroEmoji = (form.emoji && String(form.emoji).trim()) || previewCategory.emoji;
  const whenPreview = parseLocalDateTime(form.date, form.time);
  const previewAddress = (form.locationAddress || locationName || 'Map location').trim();
  const previewGenderBadge =
    form.allowedGender === 'male'
      ? { label: 'Boys only', cls: 'bg-blue-500/75' }
      : form.allowedGender === 'female'
        ? { label: 'Girls only', cls: 'bg-pink-500/75' }
        : { label: 'Open to all', cls: 'bg-purple-500/75' };

  const todayStr = toLocalDateValue(new Date());
  const tomorrowStr = toLocalDateValue(addDaysLocal(new Date(), 1));

  const applyQuickWhen = (mode) => {
    setCustomDateExpanded(false);
    if (mode === 'today') {
      const now = new Date();
      const slot = ensureFutureLocalSlot(todayStr, toLocalTimeValue(now));
      setForm((prev) => ({ ...prev, ...slot }));
      return;
    }
    if (mode === 'tomorrow') {
      setForm((prev) => {
        const timeBase = prev.time || '10:00';
        const slot = ensureFutureLocalSlot(tomorrowStr, timeBase);
        return { ...prev, ...slot };
      });
    }
  };

  const hostedCreated = user?.hostedActivitiesCreatedCount ?? 0;
  const isFaceVerified = user?.faceVerified === true;
  const canUseCreateForm =
    isEditMode || isFaceVerified || hostedCreated < UNVERIFIED_HOST_ACTIVITY_LIMIT;

  const whenStepPreview = (() => {
    if (customDateExpanded && !form.date && form.time) {
      const parts = form.time.split(':');
      const hh = parseInt(parts[0], 10) || 0;
      const mm = parseInt(parts[1], 10) || 0;
      const t = new Date();
      t.setHours(hh, mm, 0, 0);
      return { text: `Pick a date at ${format(t, 'h:mm a')}`, muted: false };
    }
    const dt = parseLocalDateTime(form.date, form.time);
    if (!form.date || !form.time || !dt) {
      if (customDateExpanded && form.date && !form.time) {
        return { text: 'Pick a time', muted: true };
      }
      return { text: 'Pick a date & time', muted: true };
    }
    const timePart = format(dt, 'h:mm a');
    if (form.date === todayStr) return { text: `Today at ${timePart}`, muted: false };
    if (form.date === tomorrowStr) return { text: `Tomorrow at ${timePart}`, muted: false };
    return { text: `${format(dt, 'EEE, MMM d')} at ${timePart}`, muted: false };
  })();

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-sky-50/95 via-white to-indigo-50/50 dark:from-surface-950 dark:via-surface-950 dark:to-slate-950">
      <Header />

      <div className="mx-auto w-full max-w-lg px-4 pb-32 pt-4 sm:max-w-xl sm:px-6 lg:max-w-2xl lg:pb-10 lg:pt-6">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-sky-600/90 dark:text-sky-400/90">
          Step {step} of 5
        </p>
        <h1 className="mt-2 text-center text-2xl font-extrabold tracking-tight text-surface-900 dark:text-white sm:text-3xl">
          {isEditMode ? 'Edit vibe' : 'Start a Vibe'}
        </h1>

        {!isEditMode && user && !isFaceVerified && (() => {
          const usedSlots = Math.min(hostedCreated, UNVERIFIED_HOST_ACTIVITY_LIMIT);
          const slotsLeft = Math.max(0, UNVERIFIED_HOST_ACTIVITY_LIMIT - hostedCreated);
          const progressPct = Math.min(100, (usedSlots / UNVERIFIED_HOST_ACTIVITY_LIMIT) * 100);
          return (
            <aside className="mt-3 sm:mt-4" aria-label="Verification and hosting limits">
              <div className="overflow-hidden rounded-xl border border-sky-200/70 bg-white shadow-[0_8px_28px_-10px_rgba(14,116,188,0.2)] ring-1 ring-sky-500/[0.06] dark:border-sky-500/25 dark:bg-surface-900 dark:shadow-[0_10px_32px_-12px_rgba(0,0,0,0.5)] dark:ring-sky-400/10 sm:rounded-2xl">
                <div className="p-3 sm:p-4">
                  <div className="flex gap-2.5 sm:gap-3">
                    <div className="relative shrink-0">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-600 text-white shadow-md shadow-sky-500/35 sm:h-11 sm:w-11">
                        <ShieldCheck className="h-5 w-5 sm:h-[22px] sm:w-[22px]" strokeWidth={2.1} />
                      </div>
                      <span
                        className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-600 shadow-sm ring-1 ring-white dark:bg-amber-500/25 dark:text-amber-200 dark:ring-surface-900"
                        aria-hidden
                      >
                        <Sparkles className="h-2 w-2" strokeWidth={2.5} />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <h3 className="text-sm font-bold leading-tight text-surface-900 dark:text-white sm:text-[15px]">
                          Become a Verified Host
                        </h3>
                       
                      </div>
                      <p className="mt-1 text-[11px] leading-snug text-surface-600 dark:text-surface-400 sm:mt-1.5 sm:text-xs">
                        Host unlimited vibes &amp; attract more joiners. People prefer joining verified hosts{' '}
                        <span aria-hidden>✨</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/onboarding')}
                        className="mt-2.5 inline-flex w-full max-w-[168px] items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 py-2 px-3 text-xs font-bold text-white shadow-sm shadow-sky-600/20 transition-[transform,filter] duration-200 hover:brightness-105 active:scale-[0.99] sm:mt-3 sm:max-w-none sm:w-auto sm:px-4 sm:text-[13px]"
                      >
                        Verify Now
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="border-t border-sky-100/90 bg-gradient-to-b from-slate-50/95 to-slate-100/80 px-3 py-2 dark:border-sky-500/15 dark:from-surface-800/50 dark:to-surface-900/80 sm:px-4 sm:py-2"
                  aria-live="polite"
                >
                  <div className="flex flex-wrap items-center justify-between gap-1.5 text-[11px] sm:text-xs">
                    <span className="flex items-center gap-1 font-semibold text-surface-800 dark:text-surface-100">
                      <Zap className="h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400" strokeWidth={2.25} aria-hidden />
                      Unverified Vibes
                    </span>
                    <span className="tabular-nums">
                      <span className="font-bold text-surface-900 dark:text-white">{usedSlots}</span>
                      <span className="font-medium text-surface-500 dark:text-surface-400">
                        {' '}
                        / {UNVERIFIED_HOST_ACTIVITY_LIMIT}
                      </span>
                     
                    </span>
                  </div>
                  <div
                    className="mt-1.5 h-1 overflow-hidden rounded-full bg-surface-200/90 dark:bg-surface-700/80"
                    role="progressbar"
                    aria-valuenow={usedSlots}
                    aria-valuemin={0}
                    aria-valuemax={UNVERIFIED_HOST_ACTIVITY_LIMIT}
                    aria-label="Vibes created without verification"
                  >
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-600 transition-[width] duration-300 ease-out"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              </div>
            </aside>
          );
        })()}

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (step === 5 && reviewPublishEnabled && !loading) void handleSubmit(e);
          }}
          className={`mt-3 ${!canUseCreateForm ? 'pointer-events-none opacity-40' : ''}`}
        >
          <div className="mb-8">
            <p className="text-center text-sm font-semibold text-sky-700 dark:text-sky-300">{stepMeta.label}</p>
            <div className="mt-3 flex gap-1.5 px-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-500 ease-out ${
                    i <= step
                      ? 'bg-gradient-to-r from-sky-500 via-brand-500 to-indigo-500 shadow-sm shadow-sky-500/25'
                      : 'bg-surface-200 dark:bg-surface-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Step 1 — plan (Lovable-style category grid + title + description) */}
          <div className={step === 1 ? 'animate-create-step' : 'hidden'}>
            <h2 className="text-center text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">
              {stepMeta.headline} {stepMeta.emoji}
            </h2>
            <p className="mt-2 text-center text-sm text-surface-500 dark:text-surface-400">{stepMeta.sub}</p>
           
            <label className="mt-10 block text-sm font-bold text-surface-800 dark:text-surface-100">
              Give it a catchy title <span className="font-normal text-amber-500">✨</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              className="input-field mt-2 rounded-2xl border-surface-200/80 shadow-sm transition-shadow duration-300 focus:shadow-md focus:shadow-sky-500/10 dark:border-surface-700"
              placeholder="e.g. Morning Badminton 🏸"
            />
            <label className="mt-6 block text-sm font-bold text-surface-800 dark:text-surface-100">
              What&apos;s the vibe?
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              className="input-field mt-2 min-h-[100px] resize-y rounded-2xl border-surface-200/80 shadow-sm dark:border-surface-700"
              placeholder="Tell people what to expect..."
              rows={3}
            />
             <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => update('category', cat.id)}
                  className={`group flex flex-col items-center gap-2 rounded-2xl border-2 px-3 py-4 text-center transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98] ${
                    form.category === cat.id
                      ? 'border-sky-400 bg-sky-50/90 text-sky-900 shadow-lg shadow-sky-500/15 ring-2 ring-sky-400/25 dark:border-sky-400/60 dark:bg-sky-500/15 dark:text-sky-100'
                      : 'border-surface-200/90 bg-white/90 text-surface-700 hover:border-sky-200 dark:border-surface-700 dark:bg-surface-900/60 dark:text-surface-200 dark:hover:border-sky-500/30'
                  }`}
                >
                  <span className="text-3xl transition-transform duration-300 group-hover:scale-110">{cat.emoji}</span>
                  <span className="text-xs font-bold sm:text-sm">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2 — location: current location OR search + map */}
          <div className={step === 2 ? 'animate-create-step' : 'hidden'}>
            <h2 className="text-center text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">
              {stepMeta.headline} {stepMeta.emoji}
            </h2>
            <p className="mt-2 text-center text-sm text-surface-500 dark:text-surface-400">{stepMeta.sub}</p>

            <button
              type="button"
              onClick={() => void applyCurrentLocation()}
              disabled={locationLockedForEdit}
              className="mt-8 flex w-full items-center gap-4 rounded-2xl border border-surface-200/90 bg-white/95 px-4 py-4 text-left shadow-sm transition-all duration-300 hover:border-sky-300/80 hover:shadow-md active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-700 dark:bg-surface-900/60 dark:hover:border-sky-500/35"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-brand-600 text-white shadow-md shadow-sky-500/20">
                <Compass size={22} strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-surface-900 dark:text-white">Use my current location</span>
                <span className="mt-0.5 block truncate text-xs text-surface-500 dark:text-surface-400">
                  {(() => {
                    const unusable = new Set(['', 'Detecting...', 'Location unavailable']);
                    const hint = [locationName, deviceLocationName].find((n) => n && !unusable.has(n));
                    return hint ? `Near ${hint}` : 'Uses your device location on the map';
                  })()}
                </span>
              </div>
            </button>

            <div className="my-8 flex items-center gap-4">
              <div className="h-px flex-1 bg-surface-200/90 dark:bg-surface-700/90" />
              <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.2em] text-surface-400 dark:text-surface-500">
                Or
              </span>
              <div className="h-px flex-1 bg-surface-200/90 dark:bg-surface-700/90" />
            </div>

            <label className="mb-2 block text-sm font-bold text-surface-800 dark:text-surface-100">
              Search for a place
            </label>
            {locationLockedForEdit ? (
              <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                Someone has already joined this vibe, so the location is locked for safety. You can still edit other details.
              </div>
            ) : (
              <GooglePlacesSearch
                onPlaceSelect={handlePlaceSelect}
                onClear={() => {
                  mapPinUserAdjustedRef.current = false;
                  update('locationAddress', '');
                }}
                initialValue={form.locationAddress}
                placeholder={form.locationAddress || locationName || 'Park, café, venue…'}
              />
            )}
            <p className="mt-2 text-xs leading-relaxed text-surface-400 dark:text-surface-500">
              {locationLockedForEdit
                ? 'Location cannot be changed after someone joins this vibe.'
                : form.locationAddress
                ? 'Pin updates when you pick a result. You can still fine-tune on the map.'
                : 'Pick a search result to drop the pin, or use current location above.'}
            </p>
            {form.locationAddress?.trim() ? (
              <button
                type="button"
                onClick={() =>
                  openGoogleMapsDirectionsToDestination({
                    address: form.locationAddress,
                    coordinates: coords,
                  })
                }
                className="mt-4 flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-2xl border border-surface-200/80 bg-white/90 px-4 py-3 text-left text-sm text-surface-600 transition-all hover:border-sky-200 hover:shadow-sm dark:border-surface-700 dark:bg-surface-900/50 dark:text-surface-400 dark:hover:border-sky-500/30"
                aria-label="Open directions to this location in Google Maps"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-500/15">
                  <MapPin size={16} className="text-sky-600 dark:text-sky-400" />
                </div>
                <span className="min-w-0 flex-1 truncate">{form.locationAddress}</span>
              </button>
            ) : null}
            <div className="mt-5 overflow-hidden rounded-2xl border border-surface-200/80 shadow-sm dark:border-surface-700">
              <MapboxInlinePicker lat={coords[1]} lng={coords[0]} onCoordinatesChange={handleMapCoordinatesChange} />
            </div>
          </div>

          {/* Step 3 — when (quick date chips + time + preview) */}
          <div className={step === 3 ? 'animate-create-step' : 'hidden'}>
            <h2 className="text-center text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">
              {stepMeta.headline} {stepMeta.emoji}
            </h2>
            <p className="mt-2 text-center text-sm text-surface-500 dark:text-surface-400">{stepMeta.sub}</p>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { mode: 'today', emoji: '⚡', title: 'Today', sub: 'Right now energy' },
                { mode: 'tomorrow', emoji: '🌅', title: 'Tomorrow', sub: 'Plan ahead' },
                { mode: 'custom', emoji: '📅', title: 'Pick a date', sub: 'Choose your own' },
              ].map((chip) => {
                const isQuick =
                  !customDateExpanded &&
                  (chip.mode === 'today'
                    ? form.date === todayStr
                    : chip.mode === 'tomorrow'
                      ? form.date === tomorrowStr
                      : false);
                const isCustom =
                  chip.mode === 'custom' &&
                  Boolean(form.date) &&
                  form.date !== todayStr &&
                  form.date !== tomorrowStr;
                const selected =
                  chip.mode === 'custom' ? customDateExpanded || isCustom : isQuick;
                return (
                  <button
                    key={chip.mode}
                    type="button"
                    onClick={() =>
                      chip.mode === 'custom' ? setCustomDateExpanded(true) : applyQuickWhen(chip.mode)
                    }
                    className={`flex flex-col items-start rounded-2xl border px-4 py-4 text-left transition-all duration-300 ease-out sm:px-5 sm:py-5 ${
                      chip.mode === 'custom' ? 'col-span-2' : ''
                    } ${
                      selected
                        ? 'border-sky-400/70 bg-gradient-to-br from-sky-500 to-brand-600 text-white shadow-lg shadow-sky-500/20 ring-1 ring-white/15 dark:from-sky-600 dark:to-brand-700 dark:ring-white/10'
                        : 'border-surface-200/80 bg-white/90 text-surface-800 hover:border-sky-200/90 hover:shadow-md dark:border-surface-700 dark:bg-surface-900/50 dark:text-surface-100 dark:hover:border-sky-500/25'
                    }`}
                  >
                    <span className="text-2xl sm:text-[1.65rem]" aria-hidden>
                      {chip.emoji}
                    </span>
                    <span className={`mt-3 text-sm font-bold sm:text-base ${selected ? 'text-white' : 'text-surface-900 dark:text-white'}`}>
                      {chip.title}
                    </span>
                    <span
                      className={`mt-1 text-xs leading-snug ${
                        selected ? 'text-white/85' : 'text-surface-500 dark:text-surface-400'
                      }`}
                    >
                      {chip.sub}
                    </span>
                  </button>
                );
              })}
            </div>

            {customDateExpanded ? (
              <div className="mt-8 animate-create-step space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-bold text-surface-800 dark:text-surface-100">Date</label>
                  <div className="relative">
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={form.date}
                      min={todayStr}
                      onChange={(e) => {
                        const nextDate = e.target.value;
                        setForm((prev) => {
                          let nextTime = prev.time;
                          if (!nextTime) {
                            const slot = ensureFutureLocalSlot(nextDate, '10:00');
                            return { ...prev, date: slot.date, time: slot.time };
                          }
                          if (nextDate === todayStr && nextTime) {
                            const minT = toLocalTimeValue(new Date());
                            if (nextTime < minT) nextTime = minT;
                          }
                          return { ...prev, date: nextDate, time: nextTime };
                        });
                        if (nextDate === todayStr || nextDate === tomorrowStr) {
                          setCustomDateExpanded(false);
                        }
                      }}
                      className="input-field w-full rounded-2xl border-surface-200/90 py-4 pl-4 pr-14 text-base font-semibold shadow-sm dark:border-surface-700 [&::-webkit-calendar-picker-indicator]:hidden [&::-moz-calendar-picker-indicator]:hidden"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const el = dateInputRef.current;
                        if (!el) return;
                        if (typeof el.showPicker === 'function') el.showPicker();
                        else el.focus();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 transition-colors hover:text-sky-600 dark:text-surface-500 dark:hover:text-sky-400"
                      aria-label="Open date picker"
                    >
                      <Calendar size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-8">
              <label className="mb-2 flex items-center gap-1.5 text-sm font-bold text-surface-800 dark:text-surface-100">
                Time <span className="text-base font-normal opacity-70">⏱️</span>
              </label>
              <div className="relative">
                <input
                  ref={timeInputRef}
                  type="time"
                  value={form.time}
                  min={form.date === todayStr ? toLocalTimeValue(new Date()) : undefined}
                  onChange={(e) => {
                    let t = e.target.value;
                    if (form.date === todayStr && t) {
                      const minT = toLocalTimeValue(new Date());
                      if (t < minT) t = minT;
                    }
                    update('time', t);
                  }}
                  className="input-field w-full rounded-2xl border-surface-200/90 py-4 pl-4 pr-14 text-base font-semibold shadow-sm dark:border-surface-700 [&::-webkit-calendar-picker-indicator]:hidden [&::-moz-calendar-picker-indicator]:hidden"
                />
                <button
                  type="button"
                  onClick={() => {
                    const el = timeInputRef.current;
                    if (!el) return;
                    if (typeof el.showPicker === 'function') el.showPicker();
                    else el.focus();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 transition-colors hover:text-sky-600 dark:text-surface-500 dark:hover:text-sky-400"
                  aria-label="Choose time"
                >
                  <Clock size={20} />
                </button>
              </div>
            </div>

            <div className="mt-10 rounded-3xl border border-dashed border-sky-300/50 bg-sky-50/40 px-5 py-6 text-center dark:border-sky-500/25 dark:bg-sky-950/25">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-surface-400 dark:text-surface-500">
                Preview
              </p>
              <p
                className={`mt-2 text-lg font-bold leading-snug sm:text-xl ${
                  whenStepPreview.muted
                    ? 'text-surface-400 dark:text-surface-500'
                    : 'text-sky-800 dark:text-sky-100'
                }`}
              >
                {whenStepPreview.text}
              </p>
              
            </div>
          </div>

          {/* Step 4 — crew */}
          <div className={step === 4 ? 'animate-create-step' : 'hidden'}>
            <h2 className="text-center text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">
              {stepMeta.headline} {stepMeta.emoji}
            </h2>
            <p className="mt-2 text-center text-sm text-surface-500 dark:text-surface-400">{stepMeta.sub}</p>
            <div className="mt-8">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
                Who can join?
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { id: 'male', label: 'Only boys', sub: 'Male participants' },
                  { id: 'female', label: 'Only girls', sub: 'Female participants' },
                  { id: 'everyone', label: 'Everyone', sub: 'All are welcome' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => update('allowedGender', opt.id)}
                    className={`rounded-2xl border-2 px-4 py-3 text-left transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] ${
                      form.allowedGender === opt.id
                        ? 'border-sky-500 bg-sky-50 shadow-md shadow-sky-500/10 dark:border-sky-400 dark:bg-sky-500/15'
                        : 'border-surface-200 bg-white/90 dark:border-surface-700 dark:bg-surface-900/50'
                    }`}
                  >
                    <span className="block text-sm font-bold text-surface-900 dark:text-white">{opt.label}</span>
                    <span className="text-xs text-surface-500">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>
            <label
              className={`mt-8 flex items-center justify-between gap-4 rounded-2xl border border-surface-200 bg-white/90 px-4 py-4 transition-colors dark:border-surface-700 dark:bg-surface-900/50 ${
                isFaceVerified
                  ? 'cursor-pointer hover:border-sky-200 dark:hover:border-sky-500/30'
                  : 'cursor-not-allowed opacity-60'
              }`}
            >
              <div>
                <span className="block text-sm font-bold text-surface-900 dark:text-white">Verified users only</span>
                <span className="text-xs text-surface-500">Only face-verified users can join (Safer Crowd)</span>
              </div>
              <input
                type="checkbox"
                checked={form.verifiedOnly}
                disabled={!isFaceVerified}
                onChange={(e) => update('verifiedOnly', e.target.checked)}
                className="h-5 w-5 shrink-0 rounded border-surface-300 text-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>
            <div className="mt-8">
              <label className="mb-3 block text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
                Participants needed
              </label>
              {(() => {
                const count = clampParticipants(form.maxParticipants);
                const showCustomStepper =
                  participantsCustomOpen || !isPresetParticipantCount(form.maxParticipants);
                const customOrNonPreset = showCustomStepper;
                const presetChipCls = (active) =>
                  active
                    ? 'scale-[1.02] border-transparent bg-sky-600 text-white shadow-lg shadow-sky-600/35 dark:bg-sky-500 dark:shadow-sky-500/30'
                    : 'border-surface-200 bg-white text-surface-700 hover:border-sky-300 dark:border-surface-600 dark:bg-surface-900/50 dark:text-surface-200 dark:hover:border-sky-500/50';
                return (
                  <>
                    <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
                      {PARTICIPANT_PRESETS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            setParticipantsCustomOpen(false);
                            update('maxParticipants', n);
                          }}
                          className={`flex min-h-12 w-full items-center justify-center rounded-xl border text-sm font-bold transition-all duration-300 active:scale-[0.98] sm:aspect-square sm:max-h-none ${presetChipCls(
                            !customOrNonPreset && count === n,
                          )}`}
                        >
                          {n}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setParticipantsCustomOpen(true);
                          setForm((prev) => ({
                            ...prev,
                            maxParticipants: clampParticipants(prev.maxParticipants),
                          }));
                        }}
                        className={`col-span-3 flex min-h-12 w-full items-center justify-center rounded-xl border px-3 text-xs font-bold uppercase tracking-wide transition-all duration-300 active:scale-[0.98] sm:col-span-1 ${presetChipCls(
                          customOrNonPreset,
                        )}`}
                      >
                        Custom
                      </button>
                    </div>
                    {showCustomStepper ? (
                      <div className="mt-4 flex items-center justify-center gap-4 rounded-2xl border border-surface-200/90 bg-white/90 px-4 py-3.5 dark:border-surface-700 dark:bg-surface-900/60">
                        <button
                          type="button"
                          aria-label="Decrease participants"
                          disabled={count <= PARTICIPANTS_MIN}
                          onClick={() => bumpMaxParticipants(-1)}
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-surface-200 bg-white text-surface-800 shadow-sm transition-all hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-surface-600 dark:bg-surface-800 dark:text-surface-100"
                        >
                          <Minus size={20} strokeWidth={2.25} />
                        </button>
                        <span className="min-w-[2.5rem] text-center text-xl font-extrabold tabular-nums text-surface-900 dark:text-white">
                          {count}
                        </span>
                        <button
                          type="button"
                          aria-label="Increase participants"
                          disabled={count >= PARTICIPANTS_MAX}
                          onClick={() => bumpMaxParticipants(1)}
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-surface-200 bg-white text-surface-800 shadow-sm transition-all hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-surface-600 dark:bg-surface-800 dark:text-surface-100"
                        >
                          <Plus size={20} strokeWidth={2.25} />
                        </button>
                      </div>
                    ) : null}
                    <p className="mt-2 text-center text-[11px] text-surface-400 dark:text-surface-500">
                      Between {PARTICIPANTS_MIN} and {PARTICIPANTS_MAX} people
                    </p>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Step 5 — review */}
          <div className={step === 5 ? 'animate-create-step' : 'hidden'}>
            <h2 className="text-center text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">
              {stepMeta.headline} {stepMeta.emoji}
            </h2>
            <p className="mt-2 text-center text-sm text-surface-500 dark:text-surface-400">{stepMeta.sub}</p>

            <div className="mt-8">
              <p className="mb-3 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-surface-400 dark:text-surface-500">
                Explore preview
              </p>
              <div
                className="mx-auto w-full max-w-md overflow-hidden rounded-[20px] shadow-[0_2px_16px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] dark:bg-[#111827]/80 dark:shadow-lg dark:shadow-black/20 dark:ring-white/[0.06]"
              >
                <div className="relative h-[192px] select-none overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${previewGradient}`}>
                    <div className="absolute -top-10 -right-10 h-36 w-36 rounded-full bg-white/15 blur-2xl" />
                    <div className="absolute -bottom-6 -left-6 h-28 w-28 rounded-full bg-black/10 blur-xl" />
                    <div className="absolute left-12 top-8 h-16 w-16 rounded-full bg-white/10 blur-lg" />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="text-7xl opacity-30 drop-shadow-2xl" aria-hidden>
                        {previewHeroEmoji}
                      </span>
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/45 via-black/18 to-transparent" />

                  <div className="absolute left-3 top-3 z-[1] flex max-w-[78%] flex-wrap gap-1.5">
                    {form.verifiedOnly ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/75 px-2.5 py-1 text-[10px] font-bold text-white shadow-sm backdrop-blur-sm">
                        <ShieldCheck size={10} />
                        Verified
                      </span>
                    ) : null}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold text-white shadow-sm backdrop-blur-sm ${previewGenderBadge.cls}`}
                    >
                      {previewGenderBadge.label}
                    </span>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3 z-[1] flex items-end justify-between gap-2">
                    <span className="inline-flex max-w-[min(100%,14rem)] items-center gap-1.5 truncate rounded-full bg-black/35 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                      <span className="shrink-0">{previewCategory.emoji}</span>
                      <span className="truncate">{previewCategory.label || 'Category'}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white/95 backdrop-blur-sm">
                      Preview
                    </span>
                  </div>
                </div>

                <div className="min-w-0 bg-white p-4 dark:bg-[#111827]/90">
                  <h3 className="mb-2 line-clamp-2 min-w-0 break-words text-[15px] font-semibold leading-snug text-surface-900 dark:text-white">
                    {form.title?.trim() || 'Your vibe title'}
                  </h3>
                  {form.description?.trim() ? (
                    <p className="mb-3 line-clamp-3 text-[12.5px] leading-relaxed text-surface-500 dark:text-surface-400">
                      {form.description.trim()}
                    </p>
                  ) : null}

                  <div className="min-w-0 space-y-1.5">
                    <div className="flex min-w-0 items-center gap-2 text-[12.5px] text-surface-500 dark:text-gray-400">
                      <MapPin size={14} className="shrink-0 text-brand-400" />
                      <span className="min-w-0 flex-1 truncate">{previewAddress}</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-2 text-[12.5px] text-surface-500 dark:text-gray-400">
                      <Clock size={14} className="shrink-0 text-violet-400" />
                      <span className="min-w-0 flex-1 truncate">
                        {whenPreview ? formatActivityDate(whenPreview.toISOString()) : '—'}
                      </span>
                    </div>
                    <div className="flex min-w-0 items-center gap-2 text-[12.5px] font-medium text-surface-500 dark:text-gray-400">
                      <Users size={14} className="shrink-0 text-emerald-400" />
                      <span className="min-w-0 flex-1 truncate">
                        {formatCountNoun(form.maxParticipants, 'spot', 'spots')} open
                        {!form.verifiedOnly ? ' · Anyone can join' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`mt-12 flex flex-col gap-3 sm:mt-14 ${
              isEditMode && step < 5 ? '' : 'sm:flex-row-reverse sm:items-center sm:justify-between'
            }`}
          >
            {step < 5 ? (
              <button
                type="button"
                onClick={goNext}
                className="w-full rounded-full bg-gradient-to-r from-sky-500 via-brand-500 to-indigo-600 py-4 text-sm font-bold text-white shadow-lg shadow-sky-500/25 transition-all duration-300 hover:brightness-110 active:scale-[0.99] sm:w-auto sm:min-w-[200px]"
              >
                Continue
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || !reviewPublishEnabled}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sky-500 via-brand-500 to-indigo-600 py-4 text-sm font-bold text-white shadow-lg shadow-sky-500/25 transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[240px]"
              >
                <Sparkles size={18} />
                {loading ? (isEditMode ? 'Saving...' : 'Starting...') : isEditMode ? 'Save changes' : 'Start a Vibe'}
              </button>
            )}
            {isEditMode && step < 5 ? (
              <button
                type="button"
                onClick={saveActivity}
                disabled={loading}
                className="w-full rounded-full border-2 border-sky-500/85 bg-white py-3.5 text-sm font-bold text-sky-800 shadow-sm transition-all duration-300 hover:border-sky-600 hover:bg-sky-50 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-55 dark:border-sky-400/70 dark:bg-surface-900 dark:text-sky-200 dark:hover:bg-surface-800 sm:w-auto sm:self-center sm:min-w-[200px]"
              >
                {loading ? 'Saving...' : 'Save changes'}
              </button>
            ) : null}
            {step > 1 ? (
              <button
                type="button"
                onClick={goBack}
                className="text-center text-sm font-semibold text-surface-500 transition-colors hover:text-sky-700 dark:text-surface-400 dark:hover:text-sky-300 sm:text-left"
              >
                Back
              </button>
            ) : (
              <span className={isEditMode && step < 5 ? 'hidden' : 'hidden sm:block'} />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateActivityPage;
